'use client';

import {config} from '@config';
import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum} from '@datadog/browser-rum';
import _logger from '@lib/logger';
import {getHighestPrivilegeRole} from '@lib/user';
import {useSession} from 'next-auth/react';
import {useEffect, useRef} from 'react';

const logger = _logger.child?.({module: `datadog`}, {});

export const LogLevel = {
  INFO: 'info',
  ERROR: 'error',
  WARN: 'warn',
  DEBUG: 'debug',
} as const;

/**
 * Log a message to Datadog with the specified level and context
 */
export const logToDatadog = (
  message: string,
  level: (typeof LogLevel)[keyof typeof LogLevel] = LogLevel.INFO,
  context?: object,
) => {
  // Only log if Datadog is enabled and in a supported environment
  if (!config.datadog.enabled) {
    return;
  }

  switch (level) {
    case LogLevel.ERROR:
      datadogLogs.logger.error(message, context);
      break;
    case LogLevel.WARN:
      datadogLogs.logger.warn(message, context);
      break;
    default:
      datadogLogs.logger.info(message, context);
  }
};

/**
 * Capture an exception and send it to Datadog
 * This is a direct replacement for Sentry.captureException
 */
export const captureException = (error: Error | unknown, context?: Record<string, any>) => {
  try {
    // Log to console in development
    if (config.datadog.enableDebugLogging) {
      logger?.debug(`Error captured by Datadog:`, {error: String(error)});
    }

    // Extract error details
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorName = error instanceof Error ? error.name : `Unknown Error`;
    const errorStack = error instanceof Error ? error.stack : undefined;

    // Log to Datadog with error details and context
    datadogLogs.logger.error(
      `Exception captured`,
      {
        error_name: errorName,
        error_message: errorMessage,
        error_stack: errorStack,
        ...context,
      },
      error instanceof Error ? error : new Error(errorMessage),
    );

    // Also send to RUM if initialized
    if (window.__DD_RUM_INITIALIZED) {
      datadogRum.addError(error, {
        ...context,
      });
    }
  } catch (loggingError) {
    // Fallback if Datadog logging fails
    logger?.error(`Failed to log error to Datadog:`, {error: loggingError});
    logger?.error(`Original error:`, {error});
  }
};

/**
 * Capture a message and send it to Datadog
 * This is a direct replacement for Sentry.captureMessage
 */
export const captureMessage = (
  message: string,
  level: 'info' | 'error' | 'warn' = `info`,
  context?: Record<string, any>,
) => {
  try {
    // Log to Datadog with the appropriate level
    logToDatadog(message, level, context);

    // Also send to RUM if initialized and if it's an error or warning
    if (window.__DD_RUM_INITIALIZED && (level === `error` || level === `warn`)) {
      datadogRum.addError(message, {
        ...context,
      });
    }
  } catch (loggingError) {
    // Fallback if Datadog logging fails
    logger?.error(`Failed to log message to Datadog:`, {error: loggingError});
    logger?.error(`Original message:`, {message});
  }
};

declare global {
  interface Window {
    _DATADOG_SYNTHETICS_BROWSER?: boolean;
    __DD_LOGS_INITIALIZED?: boolean;
    __DD_RUM_INITIALIZED?: boolean;
  }
}

type TenantConfig = {
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
};

// Configuration for all test tenants
const TEST_TENANT_CONFIG: TenantConfig = {
  sessionSampleRate: 50,
  sessionReplaySampleRate: 0,
};

export const useApplicationStartLogger = (pathname: string | null) => {
  useEffect(() => {
    const apiHost = new URL(window.location.origin);
    const env = config.datadog.environment;

    logToDatadog(`Application started`, `info`, {
      environment: env,
      apiHost: apiHost.toString(),
      release: config.datadog.release,
      service: config.datadog.service,
      pathname,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only log on initial mount
};

/**
 * Hook for initializing Datadog
 * This combines the functionality of the original useDatadog and useSentry hooks
 */
export const useDatadog = () => {
  const {data: session, status} = useSession() || {};
  // Create a ref to track if Datadog has been initialized
  const datadogInitialized = useRef<boolean>(false);

  useEffect(() => {
    // Skip initialization if already done
    if (datadogInitialized.current) return;

    // Don't initialize if prerequisites aren't met
    if (!session?.user || !config || !config.datadog) {
      return;
    }

    // Get environment from config
    const env = config.datadog.environment;

    const getDatadogUserData = (user: typeof session.user, tenant: typeof session.tenant) => ({
      id: user.email,
      email: user.email,
      name: user.name || undefined,
      tenant_id: tenant.id,
      tenant_name: tenant.slug,
      tenant_display_name: tenant.displayName,
      roles: user.roles,
      role: getHighestPrivilegeRole(user.roles).role,
    });

    // Initialize logs if enabled in config
    if (config.datadog.enabled && config.datadog.clientToken) {
      try {
        // Check if Datadog Logs is already initialized to prevent multiple initializations
        // This will prevent the "DD_LOGS is already initialized" warning
        if (!window.__DD_LOGS_INITIALIZED) {
          datadogLogs.init({
            clientToken: config.datadog.clientToken,
            site: config.datadog.site as any,
            forwardErrorsToLogs: true,
            sessionSampleRate: config.datadog.sessionSampleRate,
            service: config.datadog.service,
            env,
          });
          // Mark as initialized to prevent multiple initializations
          window.__DD_LOGS_INITIALIZED = true;
        }

        // Set user and context information for logs
        if (session.user && session.tenant) {
          datadogLogs.setUser(getDatadogUserData(session.user, session.tenant));
        }
      } catch (error) {
        console.error(`Failed to initialize Datadog Logs:`, error);
      }
    }

    // Only initialize RUM if enabled in config and when authenticated
    if (
      config.datadog.enabled &&
      status === `authenticated` &&
      !window._DATADOG_SYNTHETICS_BROWSER &&
      config.datadog.clientToken &&
      config.datadog.applicationId &&
      config.datadog.environment &&
      config.datadog.release
    ) {
      const applicationId = config.datadog.applicationId;
      const clientToken = config.datadog.clientToken;

      // Get RUM configuration from config file
      const RUM_CONFIG = {
        applicationId,
        clientToken,
        site: config.datadog.site,
        service: config.datadog.service,
        defaultPrivacyLevel: `mask-user-input`,
      } as const;

      // Get test tenants from config
      const PROD_TEST_TENANTS = new Set(config.datadog.testTenants || []);

      // Check if it's a test tenant in production
      const isTestTenant = session?.tenant?.id && PROD_TEST_TENANTS.has(session.tenant.id);

      // Check if user is a linx admin
      const isLinxAdmin =
        (session.user.email && session.user.email.endsWith(`@linx.admin`)) ||
        (session.user.email && session.user.email.startsWith(`linxadmin@`));

      // Default RUM settings from config file
      const DEFAULT_CONFIG: TenantConfig = {
        sessionSampleRate: config.datadog.sessionSampleRate,
        sessionReplaySampleRate: config.datadog.sessionReplaySampleRate,
      };
      // Start with default configuration
      let rumConfig = {...DEFAULT_CONFIG};

      // Apply test tenant configuration if applicable
      if (isTestTenant) {
        rumConfig = {
          ...rumConfig,
          ...TEST_TENANT_CONFIG,
        };
      }

      // Disable session replay for linx.admin users
      if (isLinxAdmin) {
        rumConfig = {
          ...rumConfig,
          sessionReplaySampleRate: 0,
        };
      }

      try {
        // Check if Datadog RUM is already initialized to prevent multiple initializations
        if (!window.__DD_RUM_INITIALIZED) {
          datadogRum.init({
            ...RUM_CONFIG,
            env,
            trackResources: true,
            trackLongTasks: true,
            trackUserInteractions: true,
            version: config.datadog.release,
            ...rumConfig,
          });
          // Mark as initialized to prevent multiple initializations
          window.__DD_RUM_INITIALIZED = true;
        }

        // Set global context that will be attached to all RUM events
        datadogRum.setGlobalContext({
          auth_status: status,
        });

        // Set user and context information
        if (session.user && session.tenant) {
          datadogRum.setUser(getDatadogUserData(session.user, session.tenant));
        }
      } catch (error) {
        console.error(`Failed to initialize Datadog RUM:`, error);
      }
    }
    // Mark as initialized
    datadogInitialized.current = true;
  }, [session, status]);

  return {
    captureException,
    captureMessage,
    logToDatadog,
  };
};
